import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import type { MockTool } from '@/mocks/tools';
import { toolService } from '@/services/toolService';
import { getAllMachines, type MachineResponse } from '@/api/machine.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

export default function ToolNewPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);

  const [machines, setMachines] = useState<MachineResponse[]>([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    category: 'DIE' as MockTool['category'],
    type: '',
    assignedMachineId: '',
    location: '',
    lifeMethod: 'shots' as 'shots' | 'hours',
    maxShotCount: '100000',
    maxLifeHours: '2000',
    alertThresholdPercent: 80,
    nextMaintenanceDue: '5000',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchaseCost: '',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMachines() {
      try {
        const res = await getAllMachines(selectedWarehouseId || undefined);
        if (res.success && res.data) {
          const filtered = selectedWarehouseId && selectedWarehouseId !== 'ALL'
            ? res.data.filter((m: any) => m.warehouse_id === selectedWarehouseId || m.warehouseId === selectedWarehouseId)
            : res.data;
          setMachines(filtered);
        }
      } catch (err) {
        console.error('Failed to load machines:', err);
      }
    }
    fetchMachines();
  }, [selectedWarehouseId]);

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Tool Name is required'); return; }
    if (!form.type.trim()) { setError('Tool Type is required'); return; }

    setError(null);
    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        type: form.type.trim(),
        category: form.category,
        assignedMachineId: form.assignedMachineId || undefined,
        warehouseId: selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined,
        location: form.location || 'Main Workshop',
        lifeMethod: form.lifeMethod,
        maxShotCount: form.lifeMethod === 'shots' ? Number(form.maxShotCount) || 0 : 0,
        maxLifeHours: form.lifeMethod === 'hours' ? Number(form.maxLifeHours) || 0 : null,
        alertThresholdPercent: form.alertThresholdPercent,
        nextMaintenanceDue: Number(form.nextMaintenanceDue) || 0,
        purchaseDate: form.purchaseDate,
        purchaseCost: Number(form.purchaseCost) || 0,
        notes: form.notes || undefined,
      };

      const newTool = await toolService.createTool(payload);
      toast.success(`${newTool.toolNumber || 'Tool'} created successfully`);
      navigate('/manufacturing/tools');
    } catch (err: any) {
      console.error('Error saving tool:', err);
      setError(err?.response?.data?.message || 'Failed to create tool asset');
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions: { value: MockTool['category']; label: string }[] = [
    { value: 'DIE', label: 'Die' },
    { value: 'MOULD', label: 'Mould' },
    { value: 'FIXTURE', label: 'Fixture' },
    { value: 'JIGS', label: 'Jigs' },
    { value: 'CUTTING_TOOL', label: 'Cutting Tool' },
    { value: 'GAUGE', label: 'Gauge' },
  ];

  const categorySelectOptions: SearchableOption[] = categoryOptions.map((c) => ({
    value: c.value,
    label: c.label
  }));

  const machineSelectOptions: SearchableOption[] = [
    { value: '', label: 'Unassigned' },
    ...machines.map((m) => ({ value: m.id, label: m.name }))
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header & Back Button */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/manufacturing/tools')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
              title="Back to Tools"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Add New Tool</h1>
              <p className="text-sm text-[#64748b] mt-0.5">Register a new tool or die asset for life tracking</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-6 space-y-6">
              {error && (
                <div className="px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg text-rose-600" />
                  {error}
                </div>
              )}

              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-bold text-[#1e293b] mb-4 pb-2 border-b border-slate-100">
                  <i className="ri-information-line mr-2 text-blue-600" /> Basic Tool Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Tool Name <span className="text-rose-500">*</span></label>
                    <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Progressive Die - Bracket A" className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Category <span className="text-rose-500">*</span></label>
                    <SearchableSelect
                      value={form.category}
                      onChange={(val) => setForm((p) => ({ ...p, category: val as MockTool['category'] }))}
                      options={categorySelectOptions}
                      placeholder="Select Category"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Type <span className="text-rose-500">*</span></label>
                    <input type="text" value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} placeholder="e.g. Progressive Die" className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Assign to Machine</label>
                    <SearchableSelect
                      value={form.assignedMachineId}
                      onChange={(val) => setForm((p) => ({ ...p, assignedMachineId: val }))}
                      options={machineSelectOptions}
                      placeholder="Unassigned"
                      searchPlaceholder="Search machine..."
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Location</label>
                  <input type="text" value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} placeholder="e.g. Press Shop Floor A" className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
                </div>
              </div>

              {/* Life Tracking */}
              <div className="border-t border-slate-100 pt-5">
                <h3 className="text-sm font-bold text-[#1e293b] mb-4">
                  <i className="ri-pulse-line mr-2 text-indigo-600" /> Life Tracking
                </h3>
                <div className="flex items-center gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, lifeMethod: 'shots' }))}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${form.lifeMethod === 'shots' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-xs' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'}`}
                  >
                    <i className="ri-arrow-left-right-line mr-1.5" /> Shots
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, lifeMethod: 'hours' }))}
                    className={`px-4 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${form.lifeMethod === 'hours' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-xs' : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'}`}
                  >
                    <i className="ri-time-line mr-1.5" /> Hours
                  </button>
                </div>
                {form.lifeMethod === 'shots' ? (
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Max Shot Count (Life Limit)</label>
                    <input type="number" min={1} value={form.maxShotCount} onChange={(e) => setForm((p) => ({ ...p, maxShotCount: e.target.value }))} className="w-full sm:w-64 px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Max Life (Hours)</label>
                    <input type="number" min={1} value={form.maxLifeHours} onChange={(e) => setForm((p) => ({ ...p, maxLifeHours: e.target.value }))} className="w-full sm:w-64 px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
                  </div>
                )}
              </div>

              {/* Alert Threshold */}
              <div className="border-t border-slate-100 pt-5">
                <h3 className="text-sm font-bold text-[#1e293b] mb-3">
                  <i className="ri-notification-3-line mr-2 text-amber-600" /> Alert Threshold
                </h3>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={70}
                    max={95}
                    value={form.alertThresholdPercent}
                    onChange={(e) => setForm((p) => ({ ...p, alertThresholdPercent: Number(e.target.value) }))}
                    className="flex-1 max-w-xs accent-[#2563eb] cursor-pointer"
                  />
                  <span className="text-sm font-bold text-[#1e293b] w-12 text-right">{form.alertThresholdPercent}%</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1 font-medium">Alert when life usage reaches this percentage</div>
              </div>

              <div className="pt-2">
                <label className="block text-xs font-bold text-slate-700 mb-1">Next Service Due (shots)</label>
                <input type="number" min={0} value={form.nextMaintenanceDue} onChange={(e) => setForm((p) => ({ ...p, nextMaintenanceDue: e.target.value }))} className="w-full sm:w-64 px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
              </div>

              {/* Purchase Info */}
              <div className="border-t border-slate-100 pt-5">
                <h3 className="text-sm font-bold text-[#1e293b] mb-4">
                  <i className="ri-[#2563eb] ri-money-rupee-circle-line mr-2 text-emerald-600" /> Purchase Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Purchase Date</label>
                    <input type="date" value={form.purchaseDate} onChange={(e) => setForm((p) => ({ ...p, purchaseDate: e.target.value }))} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Purchase Cost (₹)</label>
                    <input type="number" min={0} value={form.purchaseCost} onChange={(e) => setForm((p) => ({ ...p, purchaseCost: e.target.value }))} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium" />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={3} maxLength={500} className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 resize-none font-medium" />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50/70">
              <button
                onClick={() => navigate('/manufacturing/tools')}
                className="px-4 py-2.5 text-sm font-semibold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={saving}
                onClick={handleSave}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-[#2563eb] rounded-xl hover:bg-blue-700 cursor-pointer disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm transition-colors"
              >
                {saving && <i className="ri-loader-4-line animate-spin" />}
                Save Tool
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}